import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Icon } from "@/components/Icon"
import { brand } from "@/assets/brand"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useCases } from "@/features/cases/hooks/useCases"
import { toCaseVM } from "@/features/cases/case-view"
import { useShellStore } from "./shell.store"
import { confirmAction } from "./confirm.store"
import { useGenerationStore } from "@/features/oio/generation.store"
import { useNiraStore } from "@/features/nira/store"
import { stateName } from "@/features/nira/types"
import { useEntitlements } from "@/features/billing/hooks/useEntitlements"
import { AccountButton } from "@/features/billing/components/AccountButton"
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser"

const RECENT_STEP_FIRST = 4
const RECENT_STEP_MORE = 5

const WORKSPACES = [
  { id: "drafting", label: "Drafting", icon: "gavel" },
  { id: "vault", label: "Vault", icon: "archive" },
  { id: "qa", label: "Q&A", icon: "qa" },
  { id: "nira", label: "Nira", icon: "book", isNew: true },
  { id: "oio-sim", label: "OIO Simulator", icon: "gavel" },
  { id: "oia-sim", label: "OIA Simulator", icon: "gavel" },
] as const

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

function shortDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

function routeFromPath(pathname: string): string {
  if (pathname.startsWith("/drafting")) return "drafting"
  if (pathname.startsWith("/vault")) return "vault"
  if (pathname.startsWith("/qa")) return "qa"
  if (pathname.startsWith("/nira")) return "nira"
  if (pathname.startsWith("/oio-sim")) return "oio-sim"
  if (pathname.startsWith("/oia-sim")) return "oia-sim"
  if (pathname.startsWith("/subscription")) return "billing"
  return "home"
}

function initials(name: string | null | undefined, email?: string): string {
  const src = (name ?? "").trim() || (email ?? "").split("@")[0] || ""
  const parts = src.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return "—"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function Nav() {
  const location = useLocation()
  const navigate = useNavigate()
  const route = routeFromPath(location.pathname)
  const { session } = useAuth()
  const { me } = useCurrentUser()
  const billingEnabled = me?.billing_enabled === true
  const { cases, activeCase, setActiveCase, deleteCase } = useCases()
  const { navMode, toggleNav } = useShellStore()
  const entitlements = useEntitlements()
  const runs = useGenerationStore((s) => s.runs)
  // The global "drafting" dot must mirror the per-case dots exactly: show it only
  // when an EXISTING case is running. Deriving from `cases` (not all store runs)
  // ignores orphaned "running" entries left by deleted cases.
  const genRunning = cases.some((c) => runs[c.id]?.status === "running")
  const rail = navMode === "rail"
  // On Nira the rail is the officer's research: "New research" and the
  // recent research threads, as the Precedent Engine design lays it out.
  const precedents = route === "nira"
  const niraMetric = entitlements.metric("nira.message")
  const billingSummary = entitlements.isLoading || !entitlements.entitlements
    ? null
    : entitlements.entitlements.exempt
      ? "Unlimited"
      : `${entitlements.subscribed ? "Pro" : "Free"}${niraMetric && niraMetric.limit !== null ? ` · ${Math.max(0, niraMetric.limit - niraMetric.used)}/${niraMetric.limit}` : ""}`
  const threads = useNiraStore((s) => s.threads)
  const activeResearch = useNiraStore((s) => s.activeId)
  // A thread that never got an answer (the service was down, the officer left)
  // is not research yet; the open one stays so it can be seen being written.
  const research = threads.filter((t) => t.turns > 0 || t.running || t.id === activeResearch)
  const requestResearch = useNiraStore((s) => s.requestNav)

  const user = session?.user
  const displayName = me?.display_name?.trim() || user?.displayName?.trim() || user?.email?.split("@")[0] || "Officer"

  const goHome = () => navigate("/ask")
  const openCase = (id: string) => {
    const thread = cases.find((c) => c.id === id)
    if (thread) setActiveCase(thread)
    navigate("/drafting")
  }

  // FOUR, THEN FIVE MORE AT A TIME. Four is what fits beside the workspaces
  // without the list taking over the rail — but a rail that stops at four with
  // nothing after it reads as "you have four cases", and the tenth was
  // unreachable from here. The list grows in place (the rail scrolls); the
  // active case is always in it, however old, so the row that says where you
  // are never disappears.
  const [shown, setShown] = useState(RECENT_STEP_FIRST)
  const visible = cases.slice(0, shown)
  const active = activeCase && !visible.some((c) => c.id === activeCase.id) ? cases.find((c) => c.id === activeCase.id) : undefined
  const recent = (active ? [...visible, active] : visible).map((t) => toCaseVM(t, user))
  const hiddenCount = cases.length - visible.length - (active ? 1 : 0)

  const requestDelete = (id: string, title: string) =>
    confirmAction({
      title: "Delete case?",
      message: `"${title}" and its draft will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete case",
      danger: true,
      onConfirm: () => deleteCase(id),
    })

  return (
    <nav className="nav" aria-label="Primary">
      <div className="nav__brand">
        <span
          className="nav__brandbox"
          onClick={goHome}
          role="button"
          tabIndex={0}
          title="Go to home"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") goHome()
          }}
        >
          <img className="mark" src={brand.mark} alt="" />
          <img className="word" src={brand.logoWord} alt="NitiAssist AI" />
        </span>
        <button
          className="nav__toggle"
          onClick={toggleNav}
          title={rail ? "Expand navigation" : "Collapse navigation"}
          aria-label={rail ? "Expand navigation" : "Collapse navigation"}
        >
          <Icon name="panelleft" size={17} />
        </button>
      </div>

      <button
        className={"nav__item" + (route === "home" ? " is-active" : "")}
        onClick={goHome}
        aria-current={route === "home" ? "page" : undefined}
        title={rail ? "Home" : undefined}
      >
        <Icon name="home" size={17} />
        <span className="lbl">Home</span>
      </button>

      <div className="nav__divider" />

      {precedents ? (
        <button className="nav__new" onClick={() => requestResearch({ kind: "new" })} title="New research">
          <Icon name="plus" size={16} />
          <span className="lbl">New research</span>
        </button>
      ) : (
        <button className="nav__new" onClick={() => navigate("/ask")} title="Open simulators">
          <Icon name="plus" size={16} />
          <span className="lbl">Open simulators</span>
        </button>
      )}

      <div className="nav__scroll">
        <div className="nav__section">
          <div className="nav__section-label">Workspaces</div>
        </div>
        {WORKSPACES.map((w) => (
          <button
            key={w.id}
            className={"nav__item" + (route === w.id ? " is-active" : "")}
            onClick={() => navigate(`/${w.id}`)}
            aria-current={route === w.id ? "page" : undefined}
            title={rail ? w.label : undefined}
          >
            <Icon name={w.icon} size={17} />
            <span className="lbl">{w.label}</span>
            {w.id === "drafting" && genRunning && (
              <span className="run-dot" title="A draft is being generated" />
            )}
            {w.id === "vault" && <span className="count">{cases.length}</span>}
            {"isNew" in w && w.isNew && <span className="pe-new">New</span>}
          </button>
        ))}

        {billingEnabled && <button
          className={"nav__item" + (route === "billing" ? " is-active" : "")}
          onClick={() => navigate("/subscription")}
          aria-current={route === "billing" ? "page" : undefined}
          title={rail ? "Billing" : undefined}
        >
          <Icon name="receipt" size={17} />
          <span className="lbl">Billing</span>
          {!rail && billingSummary && (
            <span className="count" title="Remaining / total NIRA research turns">{billingSummary}</span>
          )}
        </button>}

        {precedents && research.length > 0 && (
          <div className="nav__recent">
            <div className="nav__section">
              <div className="nav__section-label">Recent research</div>
            </div>
            {research.slice(0, 12).map((t) => (
              <div key={t.id} className="nav__case-row">
                <button
                  className={"nav__case" + (activeResearch === t.id ? " is-active" : "")}
                  onClick={() => requestResearch({ kind: "open", id: t.id })}
                >
                  <span className="nav__case-title">
                    {t.running && <span className="run-dot" title="Researching" />}
                    {t.title}
                  </span>
                  <span className="nav__case-meta">
                    {stateName(t.context?.state_code ?? "36")} · {shortDate(t.updated_at)}
                    {t.authorities ? ` · ${t.authorities} authorities` : ""}
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}

        {!precedents && recent.length > 0 && (
          <div className="nav__recent">
            <div className="nav__section">
              <div className="nav__section-label">Recent cases</div>
            </div>
            {recent.map((c) => {
              const running = runs[c.id]?.status === "running"
              return (
              <div key={c.id} className="nav__case-row">
                <button
                  className={
                    "nav__case" +
                    (route === "drafting" && activeCase?.id === c.id ? " is-active" : "")
                  }
                  onClick={() => openCase(c.id)}
                >
                  <span className="nav__case-title">
                    {running && <span className="run-dot" title="Generating" />}
                    {c.title}
                  </span>
                  <span className="nav__case-meta">
                    {c.ref} · {running ? "Drafting…" : c.statusLabel}
                  </span>
                </button>
                <button
                  className="nav__case-del"
                  title="Delete case"
                  aria-label={`Delete ${c.title}`}
                  onClick={() => requestDelete(c.id, c.title)}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
              )
            })}
            {(hiddenCount > 0 || shown > RECENT_STEP_FIRST) && (
              <div className="nav__more-row">
                {hiddenCount > 0 && (
                  <button
                    className="nav__more"
                    onClick={() => setShown((n) => n + RECENT_STEP_MORE)}
                    aria-label={`Show ${Math.min(RECENT_STEP_MORE, hiddenCount)} more cases`}
                    data-testid="nav-recent-more"
                  >
                    <Icon name="chevdown" size={13} /> Show {Math.min(RECENT_STEP_MORE, hiddenCount)} more
                  </button>
                )}
                {shown > RECENT_STEP_FIRST && (
                  <button className="nav__more" onClick={() => setShown(RECENT_STEP_FIRST)} data-testid="nav-recent-less">
                    Show less
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="nav__foot">
        <AccountButton />
        <div className="nav__user">
          <b>{displayName}</b>
          <span>Adjudicating Officer</span>
        </div>
      </div>
    </nav>
  )
}
